import { Card, Grid, Stack, CircularProgress, TextField, Autocomplete } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { putAPICall, getAPICall } from "lib/callApi";
import {
  GET_SINGLE_COMPANY,
  UPDATE_SINGLE_COMPANY,
  DATE_FORMAT,
  UPDATE_DATE_FORMAT,
  PERMISSIONS_NAME,
  UPDATE_WORKING_DAYS,
} from "lib/constants";
import BaseButton from "components/atoms/Buttons/BaseButton";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { loginUserAction } from "redux/slice/users";
import MDInput from "components/MDInput";
import { checkPermission } from "utils/HelperFunctions";
import anonymousProfile from "assets/images/sample.png";
import useWorkspaceSubscription from "hooks/useWorkspaceSubscription";
import DaysCheckboxGroup from "components/DaysCheckboxGroup";
import CircularImage from "components/molecules/Images/CircularImage";
import RolePermission from "../RolePermission";
import CompanyHolidays from "./CompanyHolidays";

const SIDE_NAV_ITEMS = [
  { label: "Edit Company Name", href: "edit_company_name", permissionName: "manage_workspace" },
  { label: "Edit Company Logo", href: "edit_company_logo", permissionName: "manage_workspace" },
  {
    label: "Edit Date Format And Working Days",
    href: "edit_date_format_and_working_days",
    permissionName: "manage_workspace",
  },
  {
    label: "Company Holidays",
    href: "company_holidays",
    permissionName: "manage_workspace",
  },
  {
    label: "Manage Permission Profiles",
    href: "manage_permission_profiles",
    permissionName: "manage_users",
  },
  { label: "Manage Subscription", href: "manage_subscription", permissionName: "manage_workspace" },
];

function CompanyAdminSettings() {
  const dispatch = useDispatch();
  const { subscription } = useWorkspaceSubscription();
  const [companyName, setCompanyName] = useState();
  const [workingDays, setWorkingDays] = useState("");
  const [dbLogo, setDBLogo] = useState(anonymousProfile);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [dateFormats, setDateFormats] = useState([]);
  const [selectedDateFormat, setSelectedDateFormat] = useState();
  const [fetchDateFormatLoading, setFetchDateFormatLoading] = useState(false);

  const companyAdmin = useSelector((state) => state.loginUser.loginUserState);
  const { companyId } = companyAdmin;

  const [sidenavItems, setSideNavItems] = useState(
    SIDE_NAV_ITEMS.filter((item) => {
      if (item.href === "manage_subscription" && !subscription?.subscriptionManagementUrl) {
        return false;
      }
      return checkPermission(item.permissionName, companyAdmin.userPermission);
    })
  );
  const [selectedCategory, setSelectedCategory] = useState(sidenavItems[0].href);

  useEffect(() => {
    const newItems = SIDE_NAV_ITEMS.filter((item) => {
      if (item.href === "manage_subscription" && !subscription?.subscriptionManagementUrl) {
        return false;
      }
      return checkPermission(item.permissionName, companyAdmin.userPermission);
    });
    setSideNavItems(newItems);
    if (!newItems.find((item) => item.href === selectedCategory)) {
      setSelectedCategory(newItems[0].href);
    }
  }, [companyAdmin.userPermission, subscription]);

  const renderSidenavItems = sidenavItems.map(({ label, href }, key) => {
    const itemKey = `item-${key}`;

    return (
      <MDBox key={itemKey} component="li" pt={key === 0 ? 0 : 1}>
        <MDTypography
          onClick={() => setSelectedCategory(href)}
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          style={{ cursor: "pointer" }}
          sx={({
            borders: { borderRadius },
            functions: { pxToRem },
            palette: { light },
            transitions,
          }) => ({
            display: "flex",
            alignItems: "center",
            borderRadius: borderRadius.md,
            padding: `${pxToRem(10)} ${pxToRem(16)}`,
            transition: transitions.create("background-color", {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.shorter,
            }),

            "&:hover": {
              backgroundColor: light.main,
            },
          })}
        >
          {label}
        </MDTypography>
      </MDBox>
    );
  });

  const fetchSingleCompany = async () => {
    try {
      const result = await getAPICall(GET_SINGLE_COMPANY + companyId, true);

      if (result?.data?.success) {
        setCompanyName(result?.data?.data?.name);
        setWorkingDays(result?.data?.data?.workingDays);
        setSelectedDateFormat({ format: result?.data?.data?.dateFormat });
        if (result?.data?.data?.logo) {
          setDBLogo(result?.data?.data?.logo || anonymousProfile);
        }
        // setNewEndDate(result?.data?.data?.endDate);
        // setNewStartDate(result?.data?.data?.startDate);
      } else if (result.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Somthing went wrong.",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchDateFormats = async () => {
    try {
      setFetchDateFormatLoading(true);
      const result = await getAPICall(DATE_FORMAT, true);

      if (result?.data?.success) {
        setDateFormats(result?.data?.data);
      } else if (result.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Somthing went wrong.",
          }),
          {
            variant: "custom",
          }
        );
      }

      setFetchDateFormatLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const updateDateFormat = async () => {
    try {
      setIsRequestLoading(true);
      const result = await putAPICall(UPDATE_DATE_FORMAT, true, {
        companyId,
        format: selectedDateFormat.format,
      });

      if (result?.data?.success) {
        dispatch(loginUserAction.updateDateFormat(selectedDateFormat.format));
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Updated",
            text: result?.data?.message,
          }),
          {
            variant: "custom",
          }
        );
      } else if (result.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsRequestLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const updateWorkingDays = async () => {
    try {
      setIsRequestLoading(true);
      const result = await putAPICall(UPDATE_WORKING_DAYS, true, {
        companyId,
        workingDays,
      });

      if (result?.data?.success) {
        dispatch(loginUserAction.updateWorkingDays(workingDays));
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Updated",
            text: result?.data?.message,
          }),
          {
            variant: "custom",
          }
        );
      } else if (result.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsRequestLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const saveName = async () => {
    if (companyName) {
      const dataToPass = {
        name: companyName,
      };
      setIsRequestLoading(true);
      const result = await putAPICall(UPDATE_SINGLE_COMPANY + companyId, true, dataToPass);

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Updated",
            text: "Your company name has been updated!",
          }),
          {
            variant: "custom",
          }
        );
      } else if (result.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.response.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsRequestLoading(false);
    }
  };

  // const updateCalendar = async () => {
  //   try {
  //     if (moment(newEndDate).diff(moment(newStartDate)) <= 0) {
  //       Swal.fire({
  //         icon: "info",
  //         confirmButtonColor: "#267FEA",
  //         title: "Information",
  //         text: "End date must be greater than start date",
  //       });
  //     } else {
  //       const dataToPass = {
  //         startDate: newStartDate,
  //         endDate: newEndDate,
  //         companyId: Number(companyId),
  //       };
  //       const result = await putAPICall(UPDATE_COMPANY_CALENDAR, true, dataToPass);

  //       if (result.data.success) {
  //         fetchSingleCompany();
  //         Swal.fire({
  //           icon: "success",
  //           confirmButtonColor: "#267FEA",
  //           title: "Updated",
  //           text: "Date has been updated successfully!",
  //         });
  //       } else {
  //         Swal.fire({
  //           icon: "error",
  //           title: "Error",
  //           text: result.data.message,
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     Swal.fire({
  //       icon: "error",
  //       confirmButtonColor: "#267FEA",
  //       title: "Error",
  //       text: "Something went wrong",
  //     });
  //   }
  // };

  useEffect(() => {
    if (checkPermission(PERMISSIONS_NAME.MANAGE_WORKSPACE, companyAdmin.userPermission)) {
      fetchSingleCompany();
      fetchDateFormats();
    }
  }, [companyAdmin.userPermission]);

  return (
    <MDBox mt={4}>
      {isRequestLoading || fetchDateFormatLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={3}>
          {/* side nav */}
          <Card
            sx={{
              borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
              position: "sticky",
              top: "1%",
            }}
          >
            <MDBox
              component="ul"
              display="flex"
              flexDirection="column"
              p={2}
              m={0}
              sx={{ listStyle: "none" }}
            >
              {renderSidenavItems}
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={9}>
          <MDBox mb={3}>
            <Grid container spacing={3}>
              {selectedCategory === "edit_company_name" && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    "& .MuiFormHelperText-root": { color: "#d32f2f" },
                  }}
                >
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h5">Company Name</MDTypography>
                    </MDBox>
                    <MDBox component="form" pb={3} px={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            fullWidth
                            label="Company Name"
                            inputProps={{ autoComplete: "" }}
                            InputLabelProps={{ shrink: true }}
                            name="name"
                            placeholder="Ex. ACME Corp."
                            error={companyName && companyName?.length === 0}
                            helperText={companyName?.length === 0 && "Please enter company name"}
                            value={companyName || ""}
                            onChange={(e) => setCompanyName(e.target.value)}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          mt={2}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <MDBox mr={2}>
                            <MDButton color="gray80">Cancel</MDButton>
                          </MDBox>
                          <MDBox>
                            <MDButton color="primaryRed" onClick={saveName}>
                              Save
                            </MDButton>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* upload company logo */}
              {selectedCategory === "edit_company_logo" && (
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h5">Company Logo</MDTypography>
                    </MDBox>
                    <MDBox component="form" pb={3} px={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={12} sx={{ display: "flex" }}>
                          <CircularImage
                            originalImgSrc={dbLogo}
                            alt="company-logo"
                            allowUpload
                            newImgPrefix={companyName?.split?.(" ")?.join?.("-")}
                            onImageUploadSuccess={fetchSingleCompany}
                            onImageRemovalSuccess={fetchSingleCompany}
                            imageEndpoint={UPDATE_SINGLE_COMPANY + companyId}
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* Edit date format and working days */}
              {selectedCategory === "edit_date_format_and_working_days" && (
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h5">Date Format</MDTypography>
                    </MDBox>
                    <MDBox component="form" pb={3} px={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            disableClearable
                            id="combo-box-demo"
                            fullWidth
                            onChange={(_, dateFormat) => {
                              setSelectedDateFormat(dateFormat);
                            }}
                            value={selectedDateFormat || ""}
                            options={dateFormats}
                            getOptionLabel={(option) => option?.format || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.format === value?.format
                            }
                            renderInput={(params) => <TextField {...params} label="Date Format" />}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          mt={2}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <MDBox>
                            <MDButton color="primaryRed" onClick={updateDateFormat}>
                              Update
                            </MDButton>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>

                  <Card sx={{ mt: 2 }}>
                    <MDBox p={3}>
                      <MDTypography variant="h5">Set Working Days</MDTypography>
                      <MDBox mt={2}>
                        <DaysCheckboxGroup
                          label="Set working days your team will input KPIs"
                          value={workingDays}
                          onChange={(cb) => {
                            const newWorkingDays = cb()?.daysOfWeek;
                            if (newWorkingDays !== workingDays) setWorkingDays(newWorkingDays);
                          }}
                          disabled="0111110"
                        />
                      </MDBox>
                      <Grid container spacing={3}>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          mt={2}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <MDBox>
                            <MDButton color="primaryRed" onClick={updateWorkingDays}>
                              Update
                            </MDButton>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* company holidays */}
              {selectedCategory === "company_holidays" && <CompanyHolidays />}

              {selectedCategory === "manage_permission_profiles" && (
                <Grid item xs={12}>
                  <Card>
                    <RolePermission />
                  </Card>
                </Grid>
              )}

              {selectedCategory === "manage_subscription" && (
                <Grid item xs={12}>
                  <Card>
                    <MDBox p={3}>
                      <MDTypography variant="h5">Subscription Management</MDTypography>
                    </MDBox>
                    <MDBox display="flex" justifyContent="center" mb={8} mt={8}>
                      <BaseButton
                        label="Manage In Salesbricks"
                        href={subscription?.subscriptionManagementUrl}
                        type="secondary"
                      />
                    </MDBox>
                  </Card>
                </Grid>
              )}

              {/* custommize calendar */}
              {/* {selectedCategory === "calendar" && (
                  <Grid item xs={12}>
                    <Card>
                      <MDBox p={3}>
                        <MDTypography variant="h5">Calendar</MDTypography>
                      </MDBox>
                      <MDBox component="form" pb={3} px={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={12} sx={{ display: "flex" }}>
                            <MDBox>
                              <MDDatePicker
                                input={{ placeholder: "Select a date" }}
                                value={newStartDate}
                                options={{
                                  // dateFormat: "m-d-Y",
                                  parseDate: (datestr, format) =>
                                    moment(datestr, format, true).toDate(),
                                  formatDate: (date) => displayDate(date, dateFormat),
                                }}
                                onChange={(value) => {
                                  const myDate = new Date(value[0]);
                                  myDate.setHours(new Date().getHours(), new Date().getMinutes());
                                  setNewStartDate(myDate);
                                }}
                              />
  
                              <MDTypography variant="caption" p={2}>
                                to
                              </MDTypography>
  
                              <MDDatePicker
                                input={{ placeholder: "Select a date" }}
                                value={newEndDate}
                                options={{
                                  // dateFormat: "m-d-Y",
                                  parseDate: (datestr, format) =>
                                    moment(datestr, format, true).toDate(),
                                  formatDate: (date) => displayDate(date, dateFormat),
                                }}
                                onChange={(value) => {
                                  const myDate = new Date(value[0]);
                                  myDate.setHours(new Date().getHours(), new Date().getMinutes());
                                  setNewEndDate(myDate);
                                }}
                              />
                            </MDBox>
                          </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            mt={2}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <MDBox>
                              <MDButton
                                disabled={isRequestLoading}
                                variant="gradient"
                                color="newColor"
                                onClick={updateCalendar}
                              >
                                {isRequestLoading ? "Loading" : "Update"}
                              </MDButton>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  </Grid>
                )} */}
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default CompanyAdminSettings;
